// ---------------------------------------------------------------------------
// Monitoring Dashboard — Perpetual KYC/KYB monitoring overview
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { AlertCard } from '../components/AlertCard';
import { MonitoringScheduleList } from '../components/MonitoringScheduleList';

// ---------------------------------------------------------------------------
// Alert severity summary cards
// ---------------------------------------------------------------------------

interface SeverityCardProps {
  label: string;
  count: number;
  colorClass: string;
}

function SeverityCard({ label, count, colorClass }: SeverityCardProps) {
  return (
    <div className="bg-surface-raised rounded-sm border border-hairline p-4 border border-hairline">
      <p className="text-xs font-medium text-ink-muted uppercase tracking-wider">{label}</p>
      <p className={`mt-1 font-mono text-2xl font-medium tabular-nums ${colorClass}`}>{count}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard page
// ---------------------------------------------------------------------------

interface DashboardState {
  alerts: admin.MonitoringAlertResponse[];
  schedules: admin.MonitoringScheduleResponse[];
  stats: admin.AlertStatsResponse | null;
  loading: boolean;
  error: string | null;
}

export function MonitoringDashboardPage() {
  const { t } = useTranslation('admin');
  const [state, setState] = useState<DashboardState>({
    alerts: [],
    schedules: [],
    stats: null,
    loading: true,
    error: null,
  });

  const loadData = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    // Fetch alerts, schedules, and stats in parallel
    Promise.all([
      fetchAlerts(),
      fetchSchedules(),
      fetchAlertStats(),
    ])
      .then(([alerts, schedules, stats]) => {
        setState({
          alerts,
          schedules,
          stats,
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : t('monitoringDashboard.errors.loadFailed');
        setState((prev) => ({ ...prev, loading: false, error: message }));
      });
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAlertAction = useCallback(
    (alertId: string, status: 'acknowledged' | 'resolved' | 'dismissed') => {
      const resolution =
        status === 'acknowledged'
          ? t('monitoringDashboard.messages.acknowledgedBy')
          : t('monitoringDashboard.messages.resolvedBy', { status });
      resolveAlertApi(alertId, status, resolution)
        .then(() => loadData())
        .catch(() => {
          // Best-effort reload on failure
          loadData();
        });
    },
    [loadData, t],
  );

  const handlePauseSchedule = useCallback(
    (scheduleId: string) => {
      updateScheduleApi(scheduleId, { status: 'paused' })
        .then(() => loadData())
        .catch(() => loadData());
    },
    [loadData],
  );

  const handleResumeSchedule = useCallback(
    (scheduleId: string) => {
      updateScheduleApi(scheduleId, { status: 'active' })
        .then(() => loadData())
        .catch(() => loadData());
    },
    [loadData],
  );

  if (state.loading) return <LoadingState rows={6} />;
  if (state.error) return <ErrorState message={state.error} onRetry={loadData} />;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-ink">{t('monitoringDashboard.headings.title')}</h1>
        <button
          type="button"
          onClick={loadData}
          className="px-3 py-1.5 text-sm font-medium bg-surface-sunken hover:bg-surface-sunken text-ink rounded-sm transition-colors"
        >
          {t('monitoringDashboard.actions.refresh')}
        </button>
      </div>

      {/* Severity summary cards */}
      {state.stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <SeverityCard
            label={t('monitoringDashboard.severity.critical')}
            count={state.stats.bySeverity.critical}
            colorClass="text-danger"
          />
          <SeverityCard
            label={t('monitoringDashboard.severity.high')}
            count={state.stats.bySeverity.high}
            colorClass="text-warning"
          />
          <SeverityCard
            label={t('monitoringDashboard.severity.medium')}
            count={state.stats.bySeverity.medium}
            colorClass="text-warning"
          />
          <SeverityCard
            label={t('monitoringDashboard.severity.low')}
            count={state.stats.bySeverity.low}
            colorClass="text-ink-muted"
          />
        </div>
      )}

      {/* Recent alerts */}
      <section>
        <h2 className="text-lg font-semibold text-ink mb-3">{t('monitoringDashboard.headings.recentAlerts')}</h2>
        {state.alerts.length === 0 ? (
          <div className="bg-surface-raised rounded-sm border border-hairline p-6 text-center text-ink-muted text-sm">
            {t('monitoringDashboard.messages.noAlerts')}
          </div>
        ) : (
          <div className="space-y-3">
            {state.alerts.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onAcknowledge={(id) => handleAlertAction(id, 'acknowledged')}
                onResolve={(id) => handleAlertAction(id, 'resolved')}
                onDismiss={(id) => handleAlertAction(id, 'dismissed')}
              />
            ))}
          </div>
        )}
      </section>

      {/* Active monitoring schedules */}
      <section>
        <h2 className="text-lg font-semibold text-ink mb-3">{t('monitoringDashboard.headings.schedules')}</h2>
        <MonitoringScheduleList
          schedules={state.schedules}
          onPause={handlePauseSchedule}
          onResume={handleResumeSchedule}
        />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// API helpers (thin wrappers — will be wired via api-client later)
// ---------------------------------------------------------------------------

const apiBase = import.meta.env.VITE_API_BASE_URL || '';

async function fetchAlerts(): Promise<admin.MonitoringAlertResponse[]> {
  const response = await fetch(`${apiBase}/v1/admin/monitoring/alerts?limit=20`);
  if (!response.ok) return [];
  const body = (await response.json()) as { data: admin.MonitoringAlertResponse[] };
  return body.data ?? [];
}

async function fetchSchedules(): Promise<admin.MonitoringScheduleResponse[]> {
  const response = await fetch(`${apiBase}/v1/admin/monitoring/schedules?limit=50`);
  if (!response.ok) return [];
  const body = (await response.json()) as { data: admin.MonitoringScheduleResponse[] };
  return body.data ?? [];
}

async function fetchAlertStats(): Promise<admin.AlertStatsResponse | null> {
  const response = await fetch(`${apiBase}/v1/admin/monitoring/alerts/stats`);
  if (!response.ok) return null;
  const body = (await response.json()) as { data: admin.AlertStatsResponse };
  return body.data ?? null;
}

async function resolveAlertApi(
  alertId: string,
  status: 'acknowledged' | 'resolved' | 'dismissed',
  resolution: string,
): Promise<void> {
  await fetch(`${apiBase}/v1/admin/monitoring/alerts/${alertId}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, resolution }),
  });
}

async function updateScheduleApi(
  scheduleId: string,
  updates: Record<string, unknown>,
): Promise<void> {
  await fetch(`${apiBase}/v1/admin/monitoring/schedules/${scheduleId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}
